import type { Triangle } from './types';
import { ZipBuilder } from './zip';

/** Export triangles as binary STL. Output is a Blob suitable for download. */
export function exportBinaryStl(triangles: Triangle[]): Blob {
  const buf = new ArrayBuffer(80 + 4 + 50 * triangles.length);
  const view = new DataView(buf);
  view.setUint32(80, triangles.length, true);
  let off = 84;
  for (const t of triangles) {
    view.setFloat32(off, t.normal[0], true);
    view.setFloat32(off + 4, t.normal[1], true);
    view.setFloat32(off + 8, t.normal[2], true);
    view.setFloat32(off + 12, t.a[0], true);
    view.setFloat32(off + 16, t.a[1], true);
    view.setFloat32(off + 20, t.a[2], true);
    view.setFloat32(off + 24, t.b[0], true);
    view.setFloat32(off + 28, t.b[1], true);
    view.setFloat32(off + 32, t.b[2], true);
    view.setFloat32(off + 36, t.c[0], true);
    view.setFloat32(off + 40, t.c[1], true);
    view.setFloat32(off + 44, t.c[2], true);
    // attribute byte count = 0
    off += 50;
  }
  return new Blob([buf], { type: 'application/vnd.ms-pki.stl' });
}

/** Export triangles as ASCII STL — larger but human-readable, accepted by every CAD tool. */
export function exportAsciiStl(triangles: Triangle[], name = 'clayforge'): Blob {
  const lines: string[] = [`solid ${name}`];
  for (const t of triangles) {
    lines.push(`  facet normal ${t.normal[0]} ${t.normal[1]} ${t.normal[2]}`);
    lines.push('    outer loop');
    lines.push(`      vertex ${t.a[0]} ${t.a[1]} ${t.a[2]}`);
    lines.push(`      vertex ${t.b[0]} ${t.b[1]} ${t.b[2]}`);
    lines.push(`      vertex ${t.c[0]} ${t.c[1]} ${t.c[2]}`);
    lines.push('    endloop');
    lines.push('  endfacet');
  }
  lines.push(`endsolid ${name}`);
  return new Blob([lines.join('\n')], { type: 'model/stl' });
}

/** Export triangles as Wavefront OBJ — accepted by Blender, MeshLab, ZBrush, every DCC.
 * De-duplicates vertices via a quantized hash to keep file size reasonable. */
export function exportObj(triangles: Triangle[], name = 'clayforge'): Blob {
  const QUANT = 1e5; // 0.01µm precision
  const key = (x: number, y: number, z: number) =>
    `${Math.round(x * QUANT)}|${Math.round(y * QUANT)}|${Math.round(z * QUANT)}`;
  const verts: string[] = [];
  const norms: string[] = [];
  const vertIdx = new Map<string, number>();
  const normIdx = new Map<string, number>();
  const faces: string[] = [];

  function addV(x: number, y: number, z: number): number {
    const k = key(x, y, z);
    let i = vertIdx.get(k);
    if (i === undefined) {
      verts.push(`v ${x} ${y} ${z}`);
      i = verts.length;          // OBJ is 1-indexed
      vertIdx.set(k, i);
    }
    return i;
  }
  function addN(x: number, y: number, z: number): number {
    const k = key(x, y, z);
    let i = normIdx.get(k);
    if (i === undefined) {
      norms.push(`vn ${x} ${y} ${z}`);
      i = norms.length;
      normIdx.set(k, i);
    }
    return i;
  }

  for (const t of triangles) {
    const a = addV(t.a[0], t.a[1], t.a[2]);
    const b = addV(t.b[0], t.b[1], t.b[2]);
    const c = addV(t.c[0], t.c[1], t.c[2]);
    const n = addN(t.normal[0], t.normal[1], t.normal[2]);
    faces.push(`f ${a}//${n} ${b}//${n} ${c}//${n}`);
  }

  const out = [
    `# ClayForge export — ${triangles.length} triangles`,
    `o ${name}`,
    ...verts,
    ...norms,
    ...faces,
    ''
  ].join('\n');
  return new Blob([out], { type: 'model/obj' });
}

/** Build the 3D/3dmodel.model XML payload — vertices/triangles per 3MF Core spec. */
function build3mfModelXml(triangles: Triangle[], name: string): string {
  const verts: string[] = [];
  const tris: string[] = [];
  const QUANT = 1e5;
  const key = (x: number, y: number, z: number) =>
    `${Math.round(x * QUANT)}|${Math.round(y * QUANT)}|${Math.round(z * QUANT)}`;
  const vIdx = new Map<string, number>();
  function addV(x: number, y: number, z: number): number {
    const k = key(x, y, z);
    let i = vIdx.get(k);
    if (i === undefined) {
      verts.push(`<vertex x="${x}" y="${y}" z="${z}"/>`);
      i = verts.length - 1;
      vIdx.set(k, i);
    }
    return i;
  }
  for (const t of triangles) {
    const a = addV(t.a[0], t.a[1], t.a[2]);
    const b = addV(t.b[0], t.b[1], t.b[2]);
    const c = addV(t.c[0], t.c[1], t.c[2]);
    tris.push(`<triangle v1="${a}" v2="${b}" v3="${c}"/>`);
  }
  // XML escape title against special chars in user-supplied filename.
  const safeName = name.replace(/[<>&"']/g, (c) =>
    ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' }[c]!));
  return `<?xml version="1.0" encoding="UTF-8"?>
<model unit="millimeter" xml:lang="en-US" xmlns="http://schemas.microsoft.com/3dmanufacturing/core/2015/02">
  <metadata name="Title">${safeName}</metadata>
  <metadata name="Application">ClayForge</metadata>
  <resources>
    <object id="1" type="model">
      <mesh>
        <vertices>${verts.join('')}</vertices>
        <triangles>${tris.join('')}</triangles>
      </mesh>
    </object>
  </resources>
  <build>
    <item objectid="1"/>
  </build>
</model>`;
}

/** Legacy raw-XML export — kept for backwards compatibility. Most slicers
 *  expect a real .3mf zip; use exportThreeMf() instead. */
export function exportXml3mf(triangles: Triangle[], name = 'clayforge'): string {
  return build3mfModelXml(triangles, name);
}

/** Export as a real 3MF (ZIP container) — STORED-mode entries.
 *  Three required files per spec:
 *    [Content_Types].xml — root MIME types
 *    _rels/.rels         — points to /3D/3dmodel.model
 *    3D/3dmodel.model    — mesh payload
 *  Verified to open in PrusaSlicer / Bambu Studio / Cura / OrcaSlicer. */
export function exportThreeMf(triangles: Triangle[], name = 'clayforge'): Blob {
  const contentTypes = `<?xml version="1.0" encoding="UTF-8"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="model" ContentType="application/vnd.ms-package.3dmanufacturing-3dmodel+xml"/>
</Types>`;
  const rels = `<?xml version="1.0" encoding="UTF-8"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Target="/3D/3dmodel.model" Id="rel0" Type="http://schemas.microsoft.com/3dmanufacturing/2013/01/3dmodel"/>
</Relationships>`;
  const model = build3mfModelXml(triangles, name);

  const zip = new ZipBuilder();
  zip.add('[Content_Types].xml', contentTypes);
  zip.add('_rels/.rels', rels);
  zip.add('3D/3dmodel.model', model);
  const buf = zip.build();
  // application/vnd.ms-package.3dmanufacturing-3dmtmodel is the registered
  // type but most browsers/OS pickers just inspect the .3mf extension.
  return new Blob([buf], { type: 'model/3mf' });
}


